import SimpleLoadingSpinner from '@src/components/SimpleLoadingSpinner';
import { useTranscription } from '@src/stores-v2/StoreContext';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import TranscriptEditorWrapper from '@src/transcript/components/TranscriptEditorWrapper';
import { useTranscriptEditorContext } from '@src/transcript/hooks/TranscriptEditorContext';
import { observer } from 'mobx-react-lite';
import React from 'react';
import styled from 'styled-components';

import type { TranscriptEditorStore } from '@src/transcript/TranscriptEditorStore';

interface TranscriptEditorContentProps {}

/**
 * TranscriptEditorContent - The actual transcript editor component
 * This is only mounted once we have a valid storyId
 */
const TranscriptEditorContent: React.FC<TranscriptEditorContentProps> =
  observer(() => {
    const videoCreator = useVideoCreatorStore();
    const transcription = useTranscription();

    const isVideoCreatorReady = videoCreator?.isInitialized;
    const isTranscriptionReady = transcription?.isTranscriptionReady;
    const isTranscriptionLoading = transcription?.isTranscriptionLoading;

    // Show loading if video creator isn't ready yet
    if (!isVideoCreatorReady) {
      return (
        <LoadingContainer>
          <SimpleLoadingSpinner text="Loading story data..." />
        </LoadingContainer>
      );
    }

    // Show loading if transcription is still loading
    if (isTranscriptionLoading) {
      return (
        <LoadingContainer>
          <SimpleLoadingSpinner text="Loading transcript data..." />
        </LoadingContainer>
      );
    }

    // Show loading if transcription isn't ready yet
    if (!isTranscriptionReady) {
      return (
        <LoadingContainer>
          <SimpleLoadingSpinner text="Preparing transcript data..." />
        </LoadingContainer>
      );
    }

    // Use the shared TranscriptEditorContext - this should be fast now
    const { editorStore, isReady: contextReady } = useTranscriptEditorContext();

    // Show loading state if context hasn't provided an editor store yet
    if (!editorStore) {
      return (
        <LoadingContainer>
          <SimpleLoadingSpinner text="Preparing transcript data..." />
        </LoadingContainer>
      );
    }

    // Show loading state if editor store is still loading
    if (editorStore.isLoading) {
      return (
        <LoadingContainer>
          <SimpleLoadingSpinner text="Preparing transcript data..." />
        </LoadingContainer>
      );
    }

    // Show loading state if context isn't ready yet (async operations still running)
    if (!contextReady || !editorStore.isReady()) {
      return (
        <LoadingContainer>
          <SimpleLoadingSpinner text="Preparing transcript data..." />
          <div style={{ fontSize: '12px', opacity: 0.7, marginTop: '10px' }}>
            Status:{' '}
            {editorStore?.isReady() ? 'Editor Ready' : 'Editor Preparing'} |
            Context: {contextReady ? 'Ready' : 'Initializing'} | Container:{' '}
            {editorStore?.container
              ? `${editorStore.container.segments.length} segments`
              : 'None'}
          </div>
        </LoadingContainer>
      );
    }

    return (
      <Container>
        <TranscriptEditorWrapper
          editorStore={editorStore as TranscriptEditorStore}
          standalone={false}
        />
      </Container>
    );
  });

// Styled components
const Container = styled.div`
  width: 100%;
  height: calc(100vh - 70px); /* Adjust as needed to fit in the ContentStudio */
  overflow: hidden;
  margin-top: -15px;
  margin-bottom: 0px;
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 300px;
  gap: 20px;
`;

export default TranscriptEditorContent;
